import React, { useEffect, useMemo, useState, useRef } from 'react';
import { Layout, Empty, Row, Col, Typography, Avatar, Spin } from 'antd';
import { UserOutlined } from '@ant-design/icons';
import MessageList from './MessageList/MessageList';
import { useTheme } from '../../../Shared/GlobalContext/ThemeContext';
import { useTranslation } from 'react-i18next';
import CountdownPill from '../../../Shared/CountdownPill';

const { Content } = Layout;
const { Text } = Typography;

// Typing animation keyframes
const typingAnimationStyle = `
  @keyframes typingAnimation {
    0% { transform: translateY(0); }
    50% { transform: translateY(-5px); }
    100% { transform: translateY(0); }
  }
`;

// Enhanced Typing indicator component with local styles - positioned on right (agent) side
const TypingIndicator = ({ isDarkMode }) => {
    const { t } = useTranslation();

    const bubbleStyle = {
        display: 'flex',
        alignItems: 'center',
        position: 'relative',
        backgroundColor: isDarkMode ? '#303030' : '#f0f0f0',
        borderRadius: '18px',
        padding: '10px 16px',
        maxWidth: '80%',
        marginRight: '12px',
        boxShadow: '0 1px 2px rgba(0, 0, 0, 0.1)',
    };

    const bubblePointerStyle = {
        content: '""',
        position: 'absolute',
        bottom: '8px',
        right: '-6px',
        width: '12px',
        height: '12px',
        backgroundColor: isDarkMode ? '#303030' : '#f0f0f0',
        transform: 'rotate(45deg)',
        borderRadius: '2px',
    };

    const dotBaseStyle = {
        width: '6px',
        height: '6px',
        borderRadius: '50%',
        backgroundColor: '#1890ff',
        display: 'inline-block',
        margin: '0 2px',
    };

    return (
        <div style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'flex-end',
            width: '100%',
            padding: '8px 0'
        }}>
            <style dangerouslySetInnerHTML={{ __html: typingAnimationStyle }} />
            <div style={bubbleStyle}>
                <div style={{ ...bubblePointerStyle }} />
                <div style={{ display: 'flex', alignItems: 'center' }}>
                    <div
                        style={{
                            ...dotBaseStyle,
                            animation: 'typingAnimation 1s infinite ease-in-out',
                            animationDelay: '0s'
                        }}
                    />
                    <div
                        style={{
                            ...dotBaseStyle,
                            animation: 'typingAnimation 1s infinite ease-in-out',
                            animationDelay: '0.2s'
                        }}
                    />
                    <div
                        style={{
                            ...dotBaseStyle,
                            animation: 'typingAnimation 1s infinite ease-in-out',
                            animationDelay: '0.4s'
                        }}
                    />
                    <Text style={{ marginLeft: '8px', color: '#1890ff' }}>
                        {t('agent_is_typing')}
                    </Text>
                </div>
            </div>
            <Avatar
                size="small"
                icon={<UserOutlined />}
                style={{ marginLeft: '8px' }}
            />
        </div>
    );
};

// Countdown indicator positioned similarly to typing indicator (shared pill)
const CountdownIndicator = ({ isDarkMode, seconds }) => (
    <div style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'flex-end',
        width: '100%',
        padding: '8px 0'
    }}>
        <CountdownPill
            seconds={seconds}
            isDarkMode={isDarkMode}
            size="md"
            // Restart the pulse exactly when the displayed second changes.
            animationKey={seconds}
        />
    </div>
);

const MessageContent = ({
    screens,
    selectedConversation,
    messages,
    loading,
    isInitialLoading = false,
    isFetchingMoreTop = false,
    isFetchingMoreBottom = false,
    messageRefs,
    handleScroll,
    handleReplyClick,
    onReplyAction,
    setSelectedMessage,
    setDrawerVisible,
    hasMore,
    // keyboardInset, // Removed
    scrollApiRef,
    highlightedMessageId
}) => {
    const { isDarkMode } = useTheme();
    const { t } = useTranslation();
    const contentBackgroundColor = isDarkMode ? '#1f1f1f' : '#fff';
    const isWriting = selectedConversation?.is_writing;
    const appliedAt = selectedConversation?.processing_delay_applied_at;
    const appliedSeconds = selectedConversation?.processing_delay_applied_seconds;

    const [secondsUntilProcessing, setSecondsUntilProcessing] = useState(0);
    const countdownEndsAtRef = useRef(0);
    const [countdownVersion, setCountdownVersion] = useState(0);

    // Calculate countdown expiration based on stable server timestamp
    // Note: We do NOT suppress countdown based on is_writing flag here.
    // The backend may merge countdown and is_writing events together,
    // so we calculate countdown based on time and let it expire naturally.
    useEffect(() => {
        if (!appliedAt || !appliedSeconds) {
            setSecondsUntilProcessing(0);
            countdownEndsAtRef.current = 0;
            return;
        }

        // Handle potential microseconds in timestamp if not handled by Date parser (browsers usually handle simple ISO but just in case)
        const startTime = new Date(appliedAt).getTime();
        const durationMs = appliedSeconds * 1000;
        const endTime = startTime + durationMs;
        const now = Date.now();

        // Add a small buffer (2s) to account for slight clock skews or render delays
        const bufferMs = 2000;

        // Calculate remaining seconds
        const remaining = Math.max(0, Math.ceil((endTime - now + bufferMs) / 1000));

        if (remaining <= 0) {
            setSecondsUntilProcessing(0);
            countdownEndsAtRef.current = 0;
            return;
        }

        countdownEndsAtRef.current = endTime;
        setSecondsUntilProcessing(remaining);
        setCountdownVersion(prev => prev + 1);

    }, [appliedAt, appliedSeconds, selectedConversation?.id]);

    // Tick based on corrected server time
    useEffect(() => {
        if (!countdownEndsAtRef.current) return;

        const id = setInterval(() => {
            const now = window.socketService ? window.socketService.getCorrectedNow() : Date.now();
            const remainingMs = countdownEndsAtRef.current - now;
            const next = remainingMs > 0 ? Math.ceil(remainingMs / 1000) : 0;
            setSecondsUntilProcessing((prev) => (prev === next ? prev : next));
            if (next <= 0) {
                countdownEndsAtRef.current = 0;
                clearInterval(id);
            }
        }, 200);

        return () => clearInterval(id);
    }, [countdownVersion]);

    // Prepare virtual list data including temporary indicators
    const virtualListData = useMemo(() => {
        const data = [];

        // Add countdown indicator if countdown is active (takes priority over typing)
        // This handles the case where countdown and is_writing events are merged by the backend.
        // The countdown is time-based and will naturally expire when the timer runs out.
        if (secondsUntilProcessing > 0 && selectedConversation && messages.length > 0) {
            data.push({
                id: 'countdown-indicator',
                type: 'countdown-indicator',
                isTemporary: true,
                isDarkMode,
                seconds: secondsUntilProcessing
            });
        } else if (isWriting && selectedConversation) {
            // Add typing indicator only if countdown is done/not active
            data.push({
                id: 'typing-indicator',
                type: 'typing-indicator',
                isTemporary: true,
                isDarkMode
            });
        }

        // Add all messages after the indicators
        data.push(...messages);

        return data;
    }, [messages, isWriting, selectedConversation, secondsUntilProcessing, isDarkMode]);

    return (
        <div style={{ position: 'relative', height: '100%', overflow: 'hidden', display: 'flex', flexDirection: 'column', minHeight: 0 }}>
            <Content
                theme={isDarkMode ? 'dark' : 'light'}
                style={contentStyle(screens, contentBackgroundColor)}
            >
                {!selectedConversation || (!messages.length && !isWriting) ? (
                    <EmptyState isDarkMode={isDarkMode} />
                ) : (
                    <>
                        <MessageList
                            messages={virtualListData}
                            loading={loading}
                            messageRefs={messageRefs}
                            handleReplyClick={handleReplyClick}
                            onReplyAction={onReplyAction}
                            setSelectedMessage={setSelectedMessage}
                            setDrawerVisible={setDrawerVisible}
                            screens={screens}
                            onScroll={handleScroll}
                            bottomInset={(screens.xs ? 12 : 24)}
                            hasMore={hasMore}
                            conversationId={selectedConversation?.id}
                            TypingIndicator={TypingIndicator}
                            CountdownIndicator={CountdownIndicator}
                            scrollApiRef={scrollApiRef}
                            isFetchingMoreTop={isFetchingMoreTop}
                            isFetchingMoreBottom={isFetchingMoreBottom}
                            highlightedMessageId={highlightedMessageId}
                        />
                    </>
                )}
            </Content>
            {selectedConversation && isInitialLoading && !isWriting && (
                <div
                    style={{
                        position: 'absolute',
                        inset: 0,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        background: isDarkMode ? 'rgba(0, 0, 0, 0.25)' : 'rgba(255, 255, 255, 0.6)',
                        zIndex: 3
                    }}
                >
                    <Spin size="large" />
                </div>
            )}
        </div>
    );
};

const contentStyle = (screens, contentBackgroundColor) => ({
    display: 'flex',
    flexDirection: 'column-reverse',
    flex: 1,
    minHeight: 0,
    backgroundColor: contentBackgroundColor,
});

const EmptyState = ({ isDarkMode }) => {
    const { t } = useTranslation();
    return (
        <Row justify="center" align="middle" style={{ height: '100%' }}>
            <Col>
                <Empty
                    description={t('select_conversation_to_view_messages')}
                    imageStyle={{
                        filter: isDarkMode ? 'invert(0.8)' : 'none'
                    }}
                />
            </Col>
        </Row>
    );
};

export default MessageContent;
